export interface Position {
  id: string;
  title: string;
  team: string | null;
  hiringManagerId: string | null;
  hiringManagerName: string | null;
  status: "open" | "on_hold" | "filled" | "closed";
  priority: "low" | "medium" | "high";
  dateOpened: string | null;
  targetStartDate: string | null;
  notes: string | null;
  candidateCount: number;
}

export interface Candidate {
  id: string;
  positionId: string;
  name: string;
  email: string | null;
  phone: string | null;
  resumeUrl: string | null;
  source: string | null;
  stage: "applied" | "phone_screen" | "interview" | "offer" | "hired" | "rejected";
  notes: string | null;
}

export const STATUS_OPTIONS: { value: Position["status"]; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "on_hold", label: "On Hold" },
  { value: "filled", label: "Filled" },
  { value: "closed", label: "Closed" },
];

export const PRIORITY_OPTIONS: { value: Position["priority"]; label: string }[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

export const STAGE_OPTIONS: { value: Candidate["stage"]; label: string }[] = [
  { value: "applied", label: "Applied" },
  { value: "phone_screen", label: "Phone Screen" },
  { value: "interview", label: "Interview" },
  { value: "offer", label: "Offer" },
  { value: "hired", label: "Hired" },
  { value: "rejected", label: "Rejected" },
];

const STATUS_LABELS = Object.fromEntries(STATUS_OPTIONS.map((o) => [o.value, o.label])) as Record<
  Position["status"],
  string
>;
const PRIORITY_LABELS = Object.fromEntries(
  PRIORITY_OPTIONS.map((o) => [o.value, o.label]),
) as Record<Position["priority"], string>;
const STAGE_LABELS = Object.fromEntries(STAGE_OPTIONS.map((o) => [o.value, o.label])) as Record<
  Candidate["stage"],
  string
>;

export function statusLabel(status: Position["status"]): string {
  return STATUS_LABELS[status];
}
export function priorityLabel(priority: Position["priority"]): string {
  return PRIORITY_LABELS[priority];
}
export function stageLabel(stage: Candidate["stage"]): string {
  return STAGE_LABELS[stage];
}

export const STATUS_BADGE_CLASS: Record<Position["status"], string> = {
  open: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  on_hold: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  filled: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  closed: "bg-muted text-muted-foreground",
};

export const PRIORITY_BADGE_CLASS: Record<Position["priority"], string> = {
  low: "bg-muted text-muted-foreground",
  medium: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  high: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};

export const STAGE_BADGE_CLASS: Record<Candidate["stage"], string> = {
  applied: "bg-muted text-muted-foreground",
  phone_screen: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400",
  interview: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400",
  offer: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  hired: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400",
  rejected: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400",
};
